import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Target, Save, Trash2, Copy, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  fetchBudgets,
  upsertBudget,
  deleteBudget,
  copyBudgets,
  fetchCategories,
  fetchByCategory,
} from "../lib/api";
import { eur, MONTHS_IT } from "../lib/format";

const now = new Date();

function getBudgetStatus(spent, limit) {
  if (!limit || limit <= 0) return { color: "#A8A29E", label: "—" };
  const ratio = spent / limit;
  if (ratio >= 1) return { color: "#C0392B", label: "Sforato" };
  if (ratio >= 0.8) return { color: "#E67E22", label: "Attenzione" };
  return { color: "#4A6D5E", label: "Sotto soglia" };
}

export default function Budget() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [stats, setStats] = useState([]);
  const [drafts, setDrafts] = useState({}); // { categoryName: "amount" }
  const [saving, setSaving] = useState({}); // { categoryName: true/false }

  const load = useCallback(async () => {
    const [cats, buds, byCat] = await Promise.all([
      fetchCategories(),
      fetchBudgets({ year, month }),
      fetchByCategory({ year, month }),
    ]);
    setCategories(cats);
    setBudgets(buds);
    setStats(byCat);
    const next = {};
    for (const b of buds) next[b.category] = String(b.amount);
    setDrafts(next);
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const budgetByCat = useMemo(() => {
    const m = {};
    for (const b of budgets) m[b.category] = b;
    return m;
  }, [budgets]);

  const spentByCat = useMemo(() => {
    const m = {};
    for (const s of stats) m[s.category] = s.total;
    return m;
  }, [stats]);

  const handleSave = async (categoryName) => {
    const raw = drafts[categoryName];
    if (raw === undefined || raw === "" || raw === null) {
      toast.error("Inserisci un importo");
      return;
    }
    const amount = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(amount) || amount < 0) {
      toast.error("Importo non valido");
      return;
    }
    setSaving((s) => ({ ...s, [categoryName]: true }));
    try {
      await upsertBudget({ year, month, category: categoryName, amount });
      toast.success(`Budget salvato per ${categoryName}`);
      await load();
    } catch (e) {
      toast.error("Errore nel salvataggio del budget");
    } finally {
      setSaving((s) => ({ ...s, [categoryName]: false }));
    }
  };

  const handleDelete = async (categoryName) => {
    const b = budgetByCat[categoryName];
    if (!b) return;
    if (!window.confirm(`Eliminare il budget di ${categoryName} per ${MONTHS_IT[month - 1]} ${year}?`)) return;
    try {
      await deleteBudget(b.id);
      toast.success("Budget eliminato");
      await load();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleCopyFromPrev = async () => {
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    if (!window.confirm(
      `Copiare i budget da ${MONTHS_IT[prevMonth - 1]} ${prevYear} a ${MONTHS_IT[month - 1]} ${year}? I budget esistenti non saranno sovrascritti.`,
    )) return;
    try {
      const res = await copyBudgets({
        from_year: prevYear,
        from_month: prevMonth,
        to_year: year,
        to_month: month,
        overwrite: false,
      });
      if (res.source_total === 0) {
        toast.info("Nessun budget nel mese precedente");
      } else {
        toast.success(`Copiati ${res.copied} budget, ${res.skipped} saltati`);
      }
      await load();
    } catch (e) {
      toast.error("Errore nella copia dei budget");
    }
  };

  const totalBudget = useMemo(() => budgets.reduce((s, b) => s + (b.amount || 0), 0), [budgets]);
  const totalSpent = useMemo(
    () => budgets.reduce((s, b) => s + (spentByCat[b.category] || 0), 0),
    [budgets, spentByCat],
  );
  const totalRatio = totalBudget > 0 ? totalSpent / totalBudget : 0;
  const overCount = useMemo(
    () => budgets.filter((b) => (spentByCat[b.category] || 0) > b.amount).length,
    [budgets, spentByCat],
  );

  return (
    <div className="space-y-8" data-testid="budget-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Pianificazione</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">Budget mensili</h1>
          <p className="text-stone-600 mt-2 max-w-xl">
            Imposta un limite mensile di spesa per ciascuna categoria. Riceverai un avviso visivo quando le spese si avvicinano o superano la soglia.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3" data-testid="budget-period-filter">
          <span className="text-xs uppercase tracking-widest text-stone-500">Mese</span>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-[160px] rounded-xl bg-white border-stone-200" data-testid="budget-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS_IT.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)} data-testid={`budget-month-${i + 1}`}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[120px] rounded-xl bg-white border-stone-200" data-testid="budget-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[year - 2, year - 1, year, year + 1].map((y) => (
                <SelectItem key={y} value={String(y)} data-testid={`budget-year-${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleCopyFromPrev}
            className="rounded-full border-stone-300 hover:bg-stone-100"
            data-testid="budget-copy-prev"
          >
            <Copy size={16} strokeWidth={1.5} className="mr-2" /> Copia da mese precedente
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-soft p-6" data-testid="budget-total-card">
          <div className="flex items-center gap-2 text-stone-500">
            <Target size={16} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Budget totale</span>
          </div>
          <div className="mt-4 font-display text-3xl font-semibold tabular" data-testid="budget-total">
            {eur(totalBudget)}
          </div>
          <div className="text-stone-500 text-xs mt-2">{budgets.length} categorie pianificate</div>
        </div>
        <div className="card-soft p-6" data-testid="budget-spent-card">
          <div className="flex items-center gap-2 text-stone-500">
            <span className="text-xs uppercase tracking-widest">Speso (categorie con budget)</span>
          </div>
          <div className="mt-4 font-display text-3xl font-semibold tabular" data-testid="budget-spent">
            {eur(totalSpent)}
          </div>
          <div className="mt-3 h-2 w-full rounded-full bg-stone-100 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.min(100, totalRatio * 100)}%`,
                background: totalRatio >= 1 ? "#C0392B" : totalRatio >= 0.8 ? "#E67E22" : "#4A6D5E",
              }}
              data-testid="budget-spent-bar"
            />
          </div>
        </div>
        <div className="card-soft p-6" data-testid="budget-over-card">
          <div className="flex items-center gap-2 text-stone-500">
            <AlertTriangle size={16} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Sforati</span>
          </div>
          <div className="mt-4 font-display text-3xl font-semibold tabular" style={{ color: overCount > 0 ? "#C0392B" : undefined }} data-testid="budget-over-count">
            {overCount}
          </div>
          <div className="text-stone-500 text-xs mt-2">
            {overCount === 0 ? "Tutte le categorie sono sotto soglia" : `Categorie oltre il budget di ${MONTHS_IT[month - 1]}`}
          </div>
        </div>
      </div>

      {/* Per-category list */}
      <div className="card-soft p-8" data-testid="budget-list-card">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div className="flex items-center gap-3 text-stone-500">
            <Target size={18} strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-widest">Imposta budget per categoria · {MONTHS_IT[month - 1]} {year}</span>
          </div>
        </div>

        {categories.length === 0 && (
          <div className="text-stone-400 text-sm" data-testid="budget-no-categories">
            Nessuna categoria disponibile. Creane una dalla pagina Impostazioni.
          </div>
        )}

        <div className="space-y-4">
          {categories.map((c) => {
            const existing = budgetByCat[c.name];
            const spent = spentByCat[c.name] || 0;
            const limit = existing ? existing.amount : 0;
            const status = getBudgetStatus(spent, limit);
            const ratio = limit > 0 ? Math.min(1.2, spent / limit) : 0;
            const draftVal = drafts[c.name] !== undefined ? drafts[c.name] : "";

            return (
              <div
                key={c.id}
                className="rounded-2xl border border-stone-200 bg-white p-5"
                data-testid={`budget-row-${c.name}`}
              >
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-display text-lg font-semibold">{c.name}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      Speso: <span className="tabular font-semibold text-stone-700" data-testid={`budget-spent-${c.name}`}>{eur(spent)}</span>
                      {existing && (
                        <> · Budget: <span className="tabular font-semibold text-stone-700">{eur(limit)}</span></>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-widest text-stone-500">Limite €</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={draftVal}
                      onChange={(e) => setDrafts((d) => ({ ...d, [c.name]: e.target.value }))}
                      placeholder="0.00"
                      className="w-32 rounded-xl tabular"
                      data-testid={`budget-input-${c.name}`}
                    />
                    <Button
                      onClick={() => handleSave(c.name)}
                      disabled={saving[c.name]}
                      className="rounded-full bg-[#377D22] hover:bg-[#2E5F1B] h-10"
                      data-testid={`budget-save-${c.name}`}
                    >
                      <Save size={15} strokeWidth={1.5} className="mr-1.5" />
                      {existing ? "Aggiorna" : "Salva"}
                    </Button>
                    {existing && (
                      <Button
                        variant="ghost"
                        onClick={() => handleDelete(c.name)}
                        className="rounded-full text-red-600 hover:bg-red-50 h-10"
                        data-testid={`budget-delete-${c.name}`}
                        aria-label="Elimina budget"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>

                {existing && (
                  <div className="mt-4" data-testid={`budget-progress-${c.name}`}>
                    <div className="h-2.5 w-full rounded-full bg-stone-100 overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${Math.min(100, (spent / limit) * 100)}%`,
                          background: status.color,
                        }}
                        data-testid={`budget-bar-${c.name}`}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5" style={{ color: status.color }}>
                        {ratio >= 1 ? <AlertTriangle size={13} strokeWidth={2} /> : <CheckCircle2 size={13} strokeWidth={2} />}
                        <span className="font-medium">{status.label}</span>
                      </span>
                      <span className="tabular text-stone-500">
                        {limit > 0 ? `${Math.round((spent / limit) * 100)}%` : "—"} · Rimanente:{" "}
                        <span className="font-semibold text-stone-700">{eur(Math.max(0, limit - spent))}</span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
